import { useEffect } from 'react';
import { 
  Modal, Form, Input, Select, DatePicker, Switch, Space, Tooltip, 
  Row, Col, InputNumber, Button
} from 'antd';
import { 
  PlusOutlined, MinusCircleOutlined
} from '@ant-design/icons';
import type { 
  EventAlert, 
  AlertType, 
  AlertSeverity,
  NotifyBeforeUnit
} from '../../api/alertsService';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { TextArea } = Input;

const alertTypeOptions: { value: AlertType; label: string }[] = [
  { value: 'air_temperature', label: '🌡️ Air Temperature' },
  { value: 'rain_risk', label: '🌧️ Rain Risk' },
  { value: 'weather_alert', label: '⚠️ Weather Alert' },
  { value: 'track_condition', label: '🏁 Track Condition' },
  { value: 'system_status', label: '⚙️ System Status' },
  { value: 'general', label: '📢 General' },
];

const severityOptions: { value: AlertSeverity; label: string }[] = [
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
];

const notifyBeforeUnitOptions: { value: NotifyBeforeUnit; label: string }[] = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

// Available roles for alert visibility restriction (matches UserRole type)
const roleOptions: { value: string; label: string }[] = [
  { value: 'root', label: 'Root' },
  { value: 'account-admin', label: 'Account Admin' },
  { value: 'collaborator', label: 'Collaborator' },
  { value: 'developer', label: 'Developer' },
];

interface AlertModalProps {
  open: boolean;
  editingAlert: EventAlert | null;
  form: ReturnType<typeof Form.useForm>[0];
  submitting: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  selectedEvent: {
    sessions?: Array<{ session_name: string; start_time: string; end_time: string }>;
  } | null;
  circuitTimeZone?: string;
  isRootUser: boolean;
  findMatchingSessions: (
    alertStart: dayjs.Dayjs | null | undefined,
    alertEnd: dayjs.Dayjs | null | undefined
  ) => string[];
}

export const AlertModal = ({
  open,
  editingAlert,
  form,
  submitting,
  onCancel,
  onSubmit,
  selectedEvent,
  circuitTimeZone,
  isRootUser,
  findMatchingSessions,
}: AlertModalProps) => {
  // Watch start_time and end_time to auto-select sessions
  const watchedStartTime = Form.useWatch('start_time', form);
  const watchedEndTime = Form.useWatch('end_time', form);

  // Auto-select sessions when start_time or end_time changes
  useEffect(() => {
    if (!open || !watchedStartTime) return;
    
    const matchingSessions = findMatchingSessions(watchedStartTime, watchedEndTime);
    
    // Auto-update sessions if we found matching ones
    if (matchingSessions.length > 0) {
      form.setFieldValue('sessions', matchingSessions);
    }
  }, [watchedStartTime, watchedEndTime, open, findMatchingSessions, form]);

  return (
    <Modal
      title={editingAlert ? 'Edit Alert' : 'Create Alert'}
      open={open}
      onCancel={onCancel}
      onOk={onSubmit}
      confirmLoading={submitting}
      width={850}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 16 }}
      >
        {/* Row 1: Type + Severity + Title */}
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item
              name="alert_type"
              label="Type"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Select options={alertTypeOptions} placeholder="Type" />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              name="severity"
              label="Severity"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Select options={severityOptions} placeholder="Severity" />
            </Form.Item>
          </Col>
          <Col span={13}>
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, message: 'Please enter a title' }]}
            >
              <Input placeholder="Alert title" maxLength={200} />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 2: Message */}
        <Form.Item
          name="message"
          label="Message"
          rules={[{ required: true, message: 'Please enter a message' }]}
        >
          <TextArea rows={4} placeholder="Enter alert message" maxLength={2000} />
        </Form.Item>

        {/* Row 3: Start Time + End Time + Sessions */}
        <Row gutter={12}>
          <Col span={7}>
            <Form.Item
              name="start_time"
              label={`Start${circuitTimeZone ? ` (${circuitTimeZone.split('/')[1] || circuitTimeZone})` : ''}`}
              tooltip="When the alert becomes relevant"
              rules={[{ required: true, message: 'Required' }]}
            >
              <DatePicker showTime format="MMM D HH:mm" style={{ width: '100%' }} placeholder="Start" />
            </Form.Item>
          </Col>
          <Col span={7}>
            <Form.Item
              name="end_time"
              label="End"
              tooltip="When the alert expires"
              rules={[{ required: true, message: 'Required' }]}
            >
              <DatePicker showTime format="MMM D HH:mm" style={{ width: '100%' }} placeholder="End" />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              name="sessions"
              label="Sessions"
              tooltip="Auto-selected based on time range"
            >
              <Select 
                mode="multiple"
                placeholder="Sessions" 
                allowClear
                maxTagCount={3}
                options={selectedEvent?.sessions?.map(s => ({
                  value: s.session_name,
                  label: s.session_name
                })) || []}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 3.5: Roles (only visible to root users) */}
        {isRootUser && (
          <Form.Item
            name="roles"
            label="Visible to Roles"
            tooltip="Restrict which roles can see this alert. Leave empty for all roles to see it."
          >
            <Select 
              mode="multiple"
              placeholder="All roles (leave empty)" 
              allowClear
              maxTagCount={4}
              options={roleOptions}
            />
          </Form.Item>
        )}

        {/* Row 4: Switches (inline) */}
        <Space size={24} style={{ marginBottom: 16 }}>
          <Space>
            <Form.Item
              name="show_immediately"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Switch size="small" />
            </Form.Item>
            <Tooltip title="Push notification immediately upon creation">
              <span style={{ cursor: 'help' }}>Show Immediately</span>
            </Tooltip>
          </Space>
          <Space>
            <Form.Item
              name="notify_on_start"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Switch size="small" />
            </Form.Item>
            <Tooltip title="Show notification when start time is reached">
              <span style={{ cursor: 'help' }}>Notify on Start</span>
            </Tooltip>
          </Space>
          <Space>
            <Form.Item
              name="notify_on_end"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Switch size="small" />
            </Form.Item>
            <Tooltip title="Show notification when end time is reached">
              <span style={{ cursor: 'help' }}>Notify on End</span>
            </Tooltip>
          </Space>
          <Space>
            <Form.Item
              name="requires_acknowledgment"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Switch size="small" />
            </Form.Item>
            <Tooltip title="Alert reappears until acknowledged">
              <span style={{ cursor: 'help' }}>Requires Acknowledgment</span>
            </Tooltip>
          </Space>
        </Space>

        {/* Row 5: Notify Before */}
        <Form.Item 
          label="Notify Before Start Time"
          tooltip="Schedule advance notifications before the alert's start time"
        >
          <Form.List name="notify_before">
            {(fields, { add, remove }) => (
              <Space size={8} wrap>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} size={4}>
                    <Form.Item {...restField} name={[name, 'value']} style={{ marginBottom: 0 }} rules={[{ required: true }]}>
                      <InputNumber min={1} style={{ width: 60 }} placeholder="Time" />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'unit']} style={{ marginBottom: 0 }} rules={[{ required: true }]}>
                      <Select style={{ width: 100 }} options={notifyBeforeUnitOptions} placeholder="Unit" />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                  Add Notification
                </Button>
              </Space>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Modal>
  );
};
